/**
 * MVMNT Dual Size Chart Component
 * Both charts are loaded server-side by Liquid
 * JavaScript just toggles which chart is visible based on gender variant
 * NO FETCHING REQUIRED - Bypasses Cloudflare issues!
 */

class MVMNTSizeChartDual extends HTMLElement {
  constructor() {
    super();

    // Gender detection patterns
    this.GENDER_PATTERNS = {
      male: /^(male|men|mens|men's|m)$/i,
      female: /^(female|women|womens|women's|woman|w|f)$/i
    };

    // State
    this.currentGender = null;

    // Bind methods
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Get DOM elements
    this.modal = this.querySelector('[data-size-chart-modal]');
    this.modalTitle = this.querySelector('#size-chart-title');
    this.triggerButton = this.querySelector('[data-size-chart-trigger]');
    this.closeButton = this.querySelector('[data-size-chart-close]');
    this.overlay = this.querySelector('[data-size-chart-overlay]');

    // Get both chart containers
    this.maleChart = this.querySelector('.size-chart-male');
    this.femaleChart = this.querySelector('.size-chart-female');

    // Validation
    if (!this.modal || !this.triggerButton || !this.maleChart || !this.femaleChart) {
      console.error('MVMNTSizeChartDual: Required elements missing');
      return;
    }

    // Attach event listeners
    this.triggerButton.addEventListener('click', this.handleTriggerClick);
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Listen for variant changes
    document.addEventListener('variant:changed', this.handleVariantChange);

    // Detect initial gender
    this.detectInitialGender();

    console.log('MVMNTSizeChartDual: Component initialized');
    console.log('  Male chart exists:', !!this.maleChart);
    console.log('  Female chart exists:', !!this.femaleChart);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:changed', this.handleVariantChange);
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.triggerButton) {
      this.triggerButton.removeEventListener('click', this.handleTriggerClick);
    }
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }
  }

  /**
   * Detect initial gender from URL or product form
   */
  detectInitialGender() {
    // Try URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId && window.product && window.product.variants) {
      const variant = window.product.variants.find(v => v.id == variantId);
      if (variant) {
        const gender = this.extractGenderFromVariant(variant);
        if (gender) {
          this.switchToGender(gender);
          return;
        }
      }
    }

    // Try product form
    const productForm = document.querySelector('product-form');
    if (productForm && productForm.currentVariant) {
      const gender = this.extractGenderFromVariant(productForm.currentVariant);
      if (gender) {
        this.switchToGender(gender);
        return;
      }
    }

    // Default to male
    this.switchToGender('male');
  }

  /**
   * Handle variant change event
   */
  handleVariantChange(event) {
    if (!event.detail || !event.detail.variant) {
      return;
    }

    const variant = event.detail.variant;
    const gender = this.extractGenderFromVariant(variant);

    console.log('MVMNTSizeChartDual: Variant changed', {
      variantId: variant.id,
      detectedGender: gender,
      currentGender: this.currentGender
    });

    if (gender && gender !== this.currentGender) {
      this.switchToGender(gender);
    }
  }

  /**
   * Extract gender from variant
   */
  extractGenderFromVariant(variant) {
    if (!variant) return null;

    const optionValues = [
      variant.option1,
      variant.option2,
      variant.option3
    ].filter(Boolean);

    for (const optionValue of optionValues) {
      if (this.GENDER_PATTERNS.male.test(optionValue)) {
        return 'male';
      }
      if (this.GENDER_PATTERNS.female.test(optionValue)) {
        return 'female';
      }
    }

    return null;
  }

  /**
   * Switch to specific gender chart (just toggle visibility!)
   */
  switchToGender(gender) {
    if (!gender || gender === this.currentGender) {
      return;
    }

    console.log(`MVMNTSizeChartDual: Switching from "${this.currentGender}" to "${gender}"`);

    this.currentGender = gender;

    // Hide both charts first
    if (this.maleChart) {
      this.maleChart.style.display = 'none';
    }
    if (this.femaleChart) {
      this.femaleChart.style.display = 'none';
    }

    // Disable embedded style tags from both charts
    this.disableEmbeddedStyles(this.maleChart);
    this.disableEmbeddedStyles(this.femaleChart);

    // Show the correct chart and update modal data attribute
    if (gender === 'male' && this.maleChart) {
      this.maleChart.style.display = 'block';
      if (this.modal) {
        this.modal.setAttribute('data-active-gender', 'male');
      }
      if (this.modalTitle) {
        this.modalTitle.textContent = "Men's Size Chart";
      }
      // Re-enable only male chart's embedded styles
      this.enableEmbeddedStyles(this.maleChart);
      // Inject blue color overrides
      this.injectColorOverrides('male');
      console.log('✅ Showing male chart with BLUE color overrides');
    } else if (gender === 'female' && this.femaleChart) {
      this.femaleChart.style.display = 'block';
      if (this.modal) {
        this.modal.setAttribute('data-active-gender', 'female');
      }
      if (this.modalTitle) {
        this.modalTitle.textContent = "Women's Size Chart";
      }
      // Re-enable only female chart's embedded styles
      this.enableEmbeddedStyles(this.femaleChart);
      // Inject pink color overrides
      this.injectColorOverrides('female');
      console.log('✅ Showing female chart with PINK color overrides');
    }
  }

  /**
   * Disable embedded <style> tags in a chart
   */
  disableEmbeddedStyles(chartElement) {
    if (!chartElement) return;

    const styleTags = chartElement.querySelectorAll('style');
    styleTags.forEach(style => {
      style.disabled = true;
    });
  }

  /**
   * Enable embedded <style> tags in a chart
   */
  enableEmbeddedStyles(chartElement) {
    if (!chartElement) return;

    const styleTags = chartElement.querySelectorAll('style');
    styleTags.forEach(style => {
      style.disabled = false;
    });
  }

  /**
   * Inject gender-specific color overrides
   */
  injectColorOverrides(gender) {
    // Remove any existing override
    const existingOverride = document.getElementById('size-chart-color-override');
    if (existingOverride) {
      existingOverride.remove();
    }

    // Create new style tag with gender-specific colors
    const styleTag = document.createElement('style');
    styleTag.id = 'size-chart-color-override';

    if (gender === 'male') {
      styleTag.textContent = `
        .size-chart-modal[data-active-gender="male"] .measurement-label {
          background: linear-gradient(135deg, #A8D8EA 0%, #7FB3D3 100%) !important;
          color: white !important;
        }
        .size-chart-modal[data-active-gender="male"] .section-title {
          border-bottom-color: #A8D8EA !important;
        }
      `;
    } else if (gender === 'female') {
      styleTag.textContent = `
        .size-chart-modal[data-active-gender="female"] .measurement-label {
          background: linear-gradient(135deg, #FFB6C1 0%, #FF69B4 100%) !important;
          color: white !important;
        }
        .size-chart-modal[data-active-gender="female"] .section-title {
          border-bottom-color: #FFB6C1 !important;
        }
      `;
    }

    // Inject into <head>
    document.head.appendChild(styleTag);
  }

  /**
   * Open modal
   */
  openModal() {
    if (!this.modal) return;

    // Make sure correct chart is showing
    if (this.currentGender) {
      this.switchToGender(this.currentGender);
    }

    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.classList.add('is-open');

    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Listen for escape key
    document.addEventListener('keydown', this.handleKeydown);

    // Focus management
    if (this.closeButton) {
      this.closeButton.focus();
    }

    console.log('MVMNTSizeChartDual: Modal opened for gender:', this.currentGender);
  }

  /**
   * Close modal
   */
  closeModal() {
    if (!this.modal) return;

    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.classList.remove('is-open');

    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Remove escape listener
    document.removeEventListener('keydown', this.handleKeydown);

    // Return focus
    if (this.triggerButton) {
      this.triggerButton.focus();
    }

    console.log('MVMNTSizeChartDual: Modal closed');
  }

  handleTriggerClick() {
    this.openModal();
  }

  handleCloseClick() {
    this.closeModal();
  }

  handleOverlayClick() {
    this.closeModal();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }
}

// Register custom element
if (!window.customElements.get('mvmnt-size-chart-dual')) {
  window.customElements.define('mvmnt-size-chart-dual', MVMNTSizeChartDual);
  console.log('MVMNTSizeChartDual: Custom element registered');
}
